import {Component, computed, effect, ElementRef, HostListener, input, output, viewChild} from '@angular/core';
import {closingCountLabel} from '../_models/closing-corrections/closing-correction.models';

/**
 * Confirms a closing correction (#771) in a modal, built like
 * `technical-security-system/tss-reinitialize-confirm-dialog.component.ts`: backdrop and Escape cancel, focus
 * starts on „Abbrechen", and `busy` disables the confirm button while the create call is in flight.
 *
 * ### Why the consequences are spelled out one by one
 *
 * A correction withdraws a closing that the tax office may already rely on, and that cannot be undone. The
 * list names each effect the operator would otherwise only discover afterwards: the new reference, the gap in
 * the export until the new filing is accepted, the export block while the run is open, the withdrawn closing
 * staying on record, the difference to the in-app cash report for days before 20.09.2026 (Bug #751), and that
 * whether to replace a filed closing at all is a question for their tax adviser.
 *
 * ### Why backdrop and Escape still cancel
 *
 * Nothing has happened while the dialog is open, so leaving is always the safe answer. That puts it in the
 * "cancelling is free" group of the style guide's destructive-confirmation rules.
 *
 * ### Why there is no typed confirmation word
 *
 * The screen already requires a written reason before this dialog can open, which is the deliberate step the
 * typed-word pattern exists to force.
 */
@Component({
  selector: 'legal-closing-correction-confirm-dialog',
  standalone: true,
  template: `
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="closing-correction-heading"
           aria-describedby="closing-correction-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="closing-correction-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Kassenabschlüsse erneut übermitteln?
          </h2>

          <p class="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-900" data-testid="closing-correction-count">
            Ausgewählt: <strong>{{ countLabel() }}</strong>
          </p>

          <div id="closing-correction-body" class="space-y-3 text-sm text-gray-700">
            <p>Diese Aktion kann nicht rückgängig gemacht werden. Bitte beachten Sie:</p>
            <ul class="list-disc space-y-1.5 pl-5">
              <li>Jeder gewählte Kassenabschluss wird zurückgezogen und unter einer neuen Kennung erneut
                übermittelt.</li>
              <li>Bis der neue Kassenabschluss angenommen ist, fehlt dieser Tag im Datenexport. Solange die
                Übermittlung läuft, lassen sich für den betroffenen Zeitraum keine Exporte erstellen.</li>
              <li>Der zurückgezogene Kassenabschluss bleibt als zurückgezogen gespeichert und lässt sich nicht
                wiederherstellen.</li>
              <li>Für Tage vor dem 20.09.2026, an denen Artikelbestandteile getauscht wurden, zeigt die Übersicht
                „Kassen“ den Aufschlag weiterhin als eigenen Geschäftsvorfall. Der neu übermittelte
                Kassenabschluss verbucht ihn als Umsatz. Die Verfahrensdokumentation beschreibt diese
                Abweichung.</li>
              <li>Ob ein bereits übermittelter Kassenabschluss ersetzt werden darf, klären Sie bitte vorab mit
                Ihrer Steuerberatung.</li>
            </ul>
            @if (reason(); as text) {
              <p class="break-words"><span class="font-semibold text-gray-900">Grund:</span> {{ text }}</p>
            }
          </div>

          <div class="flex flex-col-reverse gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
            <button type="button" #cancelButton (click)="cancel()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed
                           disabled:opacity-30">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-red-600 px-5 py-2.5 text-sm
                           font-semibold text-white shadow-lg shadow-red-100 transition-all duration-200
                           hover:bg-red-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                           disabled:grayscale">
              {{ busy() ? 'Wird gestartet …' : 'Ja, zurückziehen und neu übermitteln' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class ClosingCorrectionConfirmDialogComponent {
  /** How many closings are about to be corrected. */
  readonly count = input.required<number>();

  /** The reason the operator wrote, repeated so they see what goes on record. */
  readonly reason = input<string>('');

  /** Disables the confirm button while the create call is in flight. */
  readonly busy = input(false);

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  protected readonly countLabel = computed(() => closingCountLabel(this.count()));

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  constructor() {
    // Focus starts on the safe action. The dialog opens from a button the operator just pressed, so a stray
    // Enter would otherwise confirm something they have not read yet.
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    // A request already in flight cannot be called back, so closing the dialog now would only hide the
    // outcome. It closes by itself when the call answers.
    if (this.busy()) return;
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
